import React, { useEffect, useMemo, useState } from "react";
import { useDrag, useDrop, useDragLayer } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { Howl } from "howler";

const ItemTypes = {
	LETTER: "letter",
};

function GameScreen({ words, initialScore, endGame, restartGame }) {
	const [currentWordIndex, setCurrentWordIndex] = useState(0);
	const [shuffledLetters, setShuffledLetters] = useState([]);
	const [placedLetters, setPlacedLetters] = useState([]);
	const [score, setScore] = useState(initialScore);
	const [mistakes, setMistakes] = useState(0);
	const [showCountdown, setShowCountdown] = useState(false);
	const [countdownNumber, setCountdownNumber] = useState(2);

	const [interactionMode, setInteractionMode] = useState("drag");
	const [selectedLetter, setSelectedLetter] = useState(null);

	// This stores temporary visual feedback for slots in tap mode.
	// Values: null | "neutral" | "correct" | "wrong"
	const [slotFeedback, setSlotFeedback] = useState([]);

	const currentWord = words[currentWordIndex];

	const correctSound = useMemo(
		() =>
			new Howl({
				src: [`${process.env.PUBLIC_URL}/sounds/correct.mp3`],
				onloaderror: function (_id, error) {
					console.error("Correct sound load error:", error);
				},
				onplayerror: function (_id, error) {
					console.error("Correct sound play error:", error);
				},
			}),
		[],
	);

	const errorSound = useMemo(
		() =>
			new Howl({
				src: [`${process.env.PUBLIC_URL}/sounds/error.mp3`],
				onloaderror: function (_id, error) {
					console.error("Error sound load error:", error);
				},
				onplayerror: function (_id, error) {
					console.error("Error sound play error:", error);
				},
			}),
		[],
	);

	useEffect(() => {
		const lettersArray = currentWord.split("");
		const extraLettersCount = Math.max(
			2,
			Math.floor(currentWord.length * 0.5),
		);
		const extraLetters = generateExtraLetters(
			extraLettersCount,
			lettersArray,
		);
		const mixedLetters = shuffleArray([...lettersArray, ...extraLetters]);

		setShuffledLetters(mixedLetters);
		setPlacedLetters(Array(currentWord.length).fill(null));
		setMistakes(0);
		setShowCountdown(false);
		setCountdownNumber(2);
		setSelectedLetter(null);
		setSlotFeedback(Array(currentWord.length).fill(null));
	}, [currentWordIndex, currentWord]);

	const generateExtraLetters = (count, baseLetters) => {
		const alphabet = "abcdefghijklmnopqrstuvwxyz".split("");
		const filteredAlphabet = alphabet.filter(
			(letter) => !baseLetters.includes(letter),
		);

		const result = [];
		for (let i = 0; i < count; i += 1) {
			const source =
				filteredAlphabet.length > 0 ? filteredAlphabet : alphabet;
			const randomLetter =
				source[Math.floor(Math.random() * source.length)];
			result.push(randomLetter);
		}
		return result;
	};

	const shuffleArray = (array) => {
		const newArray = [...array];
		for (let i = newArray.length - 1; i > 0; i -= 1) {
			const j = Math.floor(Math.random() * (i + 1));
			[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
		}
		return newArray;
	};

	const clearSlotFeedback = () => {
		setSlotFeedback(Array(currentWord.length).fill(null));
	};

	const setSingleSlotFeedback = (index, type) => {
		const next = Array(currentWord.length).fill(null);
		next[index] = type;
		setSlotFeedback(next);

		window.setTimeout(() => {
			setSlotFeedback(Array(currentWord.length).fill(null));
		}, 220);
	};

	const handleCorrectCompletion = (finalScoreValue) => {
		setShowCountdown(true);
		setCountdownNumber(2);

		let countdown = 2;

		const interval = setInterval(() => {
			countdown -= 1;

			if (countdown > 0) {
				setCountdownNumber(countdown);
				return;
			}

			clearInterval(interval);
			setShowCountdown(false);

			if (currentWordIndex + 1 < words.length) {
				setCurrentWordIndex((prev) => prev + 1);
			} else {
				endGame(finalScoreValue);
			}
		}, 1000);
	};

	const attemptPlaceLetter = (letterData, targetIndex) => {
		if (!letterData || showCountdown) {
			return;
		}

		const draggedLetter = letterData.letter;

		if (placedLetters[targetIndex] !== null) {
			return;
		}

		if (draggedLetter === currentWord[targetIndex]) {
			const nextPlacedLetters = [...placedLetters];
			nextPlacedLetters[targetIndex] = draggedLetter;
			setPlacedLetters(nextPlacedLetters);

			const nextShuffledLetters = [...shuffledLetters];
			nextShuffledLetters.splice(letterData.index, 1);
			setShuffledLetters(nextShuffledLetters);

			setSelectedLetter(null);
			setSingleSlotFeedback(targetIndex, "correct");
			correctSound.play();

			const isComplete = nextPlacedLetters.every(
				(letter) => letter !== null,
			);
			if (isComplete) {
				handleCorrectCompletion(score);
			}
		} else {
			setMistakes((prev) => prev + 1);
			setScore((prev) => prev - 1);
			setSelectedLetter(null);
			setSingleSlotFeedback(targetIndex, "wrong");
			errorSound.play();
		}
	};

	const handleDrop = (item, targetIndex) => {
		attemptPlaceLetter(item, targetIndex);
	};

	const handleLetterTap = (letter, index) => {
		if (interactionMode !== "tap" || showCountdown) {
			return;
		}

		const isSameSelected =
			selectedLetter &&
			selectedLetter.letter === letter &&
			selectedLetter.index === index;

		if (isSameSelected) {
			setSelectedLetter(null);
			clearSlotFeedback();
			return;
		}

		setSelectedLetter({ letter, index });

		// In tap mode, all empty slots turn blue.
		const nextFeedback = placedLetters.map((value) =>
			value === null ? "neutral" : null,
		);
		setSlotFeedback(nextFeedback);
	};

	const handleSlotTap = (targetIndex) => {
		if (interactionMode !== "tap" || showCountdown || !selectedLetter) {
			return;
		}

		attemptPlaceLetter(selectedLetter, targetIndex);
	};

	function DraggableLetter({ letter, index }) {
		const [{ isDragging }, drag, preview] = useDrag(
			() => ({
				type: ItemTypes.LETTER,
				item: { letter, index },
				canDrag: interactionMode === "drag" && !showCountdown,
				collect: (monitor) => ({
					isDragging: monitor.isDragging(),
				}),
			}),
			[letter, index, interactionMode, showCountdown],
		);

		useEffect(() => {
			preview(getEmptyImage(), { captureDraggingState: true });
		}, [preview]);

		const isSelected =
			selectedLetter &&
			selectedLetter.letter === letter &&
			selectedLetter.index === index;

		return (
			<div
				ref={interactionMode === "drag" ? drag : null}
				className="select-none touch-none"
				style={{
					WebkitUserSelect: "none",
					userSelect: "none",
					WebkitTouchCallout: "none",
					WebkitTapHighlightColor: "transparent",
				}}
			>
				<button
					type="button"
					onClick={() => handleLetterTap(letter, index)}
					className="w-24 h-24 md:w-32 md:h-32 mx-2 my-2 flex items-center justify-center rounded-2xl bg-transparent font-bold text-7xl md:text-9xl border-4"
					style={{
						opacity: isDragging ? 0 : 1,
						transform:
							interactionMode === "drag" && isDragging
								? "scale(1.25)"
								: "scale(1)",
						transition:
							"transform 0.08s ease, border-color 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease",
						lineHeight: 1,
						cursor: interactionMode === "drag" ? "grab" : "pointer",
						borderColor:
							interactionMode === "tap" && isSelected
								? "rgba(59, 130, 246, 0.9)"
								: "transparent",
						backgroundColor:
							interactionMode === "tap" && isSelected
								? "rgba(59, 130, 246, 0.08)"
								: "transparent",
						boxShadow:
							interactionMode === "tap" && isSelected
								? "0 0 0 6px rgba(59, 130, 246, 0.12)"
								: "none",
					}}
					aria-label={`Letter ${letter}`}
				>
					{letter}
				</button>
			</div>
		);
	}

	function DroppableSlot({ index }) {
		const [{ isOver, canDrop }, drop] = useDrop(
			() => ({
				accept: ItemTypes.LETTER,
				drop: (item) => handleDrop(item, index),
				canDrop: (item) =>
					interactionMode === "drag" &&
					placedLetters[index] === null &&
					item.letter === currentWord[index],
				collect: (monitor) => ({
					isOver: monitor.isOver(),
					canDrop: monitor.canDrop(),
				}),
			}),
			[index, placedLetters, currentWord, interactionMode],
		);

		const isDragActive = isOver && canDrop;

		let slotBackground = "transparent";
		let slotBorder = "transparent";
		let slotShadow = "none";

		if (interactionMode === "drag" && isDragActive) {
			slotBackground = "rgba(144, 238, 144, 0.35)";
			slotBorder = "rgba(34, 197, 94, 0.75)";
		}

		if (interactionMode === "tap" && slotFeedback[index] === "neutral") {
			slotBackground = "rgba(59, 130, 246, 0.10)";
			slotBorder = "rgba(59, 130, 246, 0.65)";
			slotShadow = "0 0 0 4px rgba(59, 130, 246, 0.10)";
		}

		if (interactionMode === "tap" && slotFeedback[index] === "correct") {
			slotBackground = "rgba(144, 238, 144, 0.35)";
			slotBorder = "rgba(34, 197, 94, 0.75)";
			slotShadow = "0 0 0 4px rgba(34, 197, 94, 0.10)";
		}

		if (interactionMode === "tap" && slotFeedback[index] === "wrong") {
			slotBackground = "rgba(239, 68, 68, 0.16)";
			slotBorder = "rgba(239, 68, 68, 0.80)";
			slotShadow = "0 0 0 4px rgba(239, 68, 68, 0.10)";
		}

		return (
			<button
				type="button"
				ref={interactionMode === "drag" ? drop : null}
				onClick={() => handleSlotTap(index)}
				className="mx-1 md:mx-2 flex items-center justify-center rounded-2xl select-none border-2"
				style={{
					width: `calc(90vw / ${currentWord.length})`,
					maxWidth: "140px",
					minWidth: "70px",
					height: "150px",
					backgroundColor: slotBackground,
					borderColor: slotBorder,
					boxShadow: slotShadow,
					WebkitUserSelect: "none",
					userSelect: "none",
					transition:
						"background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease",
					cursor:
						interactionMode === "tap" && selectedLetter
							? "pointer"
							: "default",
				}}
				aria-label={`Slot ${index + 1}`}
			>
				<svg
					viewBox="-10 -10 120 120"
					preserveAspectRatio="xMidYMid meet"
					style={{
						width: "100%",
						height: "100%",
						overflow: "visible",
					}}
				>
					<text
						x="50%"
						y="50%"
						dominantBaseline="middle"
						textAnchor="middle"
						fontSize="100"
						fontWeight="700"
						fontFamily="'Poppins', sans-serif"
						fill={placedLetters[index] ? "black" : "none"}
						stroke={placedLetters[index] ? "none" : "red"}
						strokeWidth="2"
						strokeDasharray="5,2"
						paintOrder="stroke"
					>
						{placedLetters[index] || currentWord[index]}
					</text>
				</svg>
			</button>
		);
	}

	function CustomDragLayer() {
		const { isDragging, item, clientOffset } = useDragLayer((monitor) => ({
			isDragging: monitor.isDragging(),
			item: monitor.getItem(),
			clientOffset: monitor.getClientOffset(),
		}));

		if (
			interactionMode !== "drag" ||
			!isDragging ||
			!item ||
			!clientOffset
		) {
			return null;
		}

		const previewWidth = window.innerWidth >= 768 ? 128 : 96;
		const previewHeight = window.innerWidth >= 768 ? 128 : 96;
		const x = clientOffset.x - previewWidth / 2;
		const y = clientOffset.y - previewHeight / 2;

		return (
			<div
				style={{
					position: "fixed",
					left: 0,
					top: 0,
					width: "100vw",
					height: "100vh",
					pointerEvents: "none",
					zIndex: 9999,
				}}
			>
				<div
					style={{
						transform: `translate(${x}px, ${y}px)`,
						WebkitTransform: `translate(${x}px, ${y}px)`,
					}}
				>
					<div
						className="w-24 h-24 md:w-32 md:h-32 flex items-center justify-center font-bold text-7xl md:text-9xl rounded-2xl"
						style={{
							WebkitUserSelect: "none",
							userSelect: "none",
							WebkitTouchCallout: "none",
							lineHeight: 1,
						}}
					>
						{item.letter}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className="w-full mx-auto px-4 relative select-none touch-none"
			style={{
				WebkitUserSelect: "none",
				userSelect: "none",
				WebkitTouchCallout: "none",
				WebkitTapHighlightColor: "transparent",
				overscrollBehavior: "none",
			}}
		>
			<div className="fixed top-4 left-4 z-50 flex gap-2">
				<button
					type="button"
					onClick={() => {
						setInteractionMode("drag");
						setSelectedLetter(null);
						clearSlotFeedback();
					}}
					className="px-4 py-2 rounded text-white"
					style={{
						backgroundColor:
							interactionMode === "drag"
								? "#2563eb"
								: "rgba(37,99,235,0.65)",
					}}
				>
					Drag Mode
				</button>

				<button
					type="button"
					onClick={() => {
						setInteractionMode("tap");
						setSelectedLetter(null);
						clearSlotFeedback();
					}}
					className="px-4 py-2 rounded text-white"
					style={{
						backgroundColor:
							interactionMode === "tap"
								? "#7c3aed"
								: "rgba(124,58,237,0.65)",
					}}
				>
					Tap Mode
				</button>
			</div>

			<button
				onClick={restartGame}
				className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded z-50"
			>
				Restart
			</button>

			<CustomDragLayer />

			<div className="flex justify-center items-center mb-8 py-4">
				{currentWord.split("").map((_, index) => (
					<DroppableSlot
						key={`${currentWord}-${index}`}
						index={index}
					/>
				))}
			</div>

			<div className="flex flex-wrap justify-center items-center">
				{shuffledLetters.map((letter, index) => (
					<DraggableLetter
						key={`${letter}-${index}-${currentWordIndex}`}
						letter={letter}
						index={index}
					/>
				))}
			</div>

			<div className="text-center mt-4 select-none">
				<p>Score: {score}</p>
				<p>Mistakes: {mistakes}</p>
				<p className="text-sm mt-2 text-gray-600">
					{interactionMode === "drag"
						? "Drag letters into the correct slots."
						: "Tap one letter, then tap any slot. Blue means selectable. Green means correct. Red means wrong."}
				</p>
			</div>

			{showCountdown && (
				<div className="fixed bottom-4 right-4 z-50">
					<p className="text-xl md:text-2xl font-medium text-red-500">
						{countdownNumber > 0 ? countdownNumber : "Change..."}
					</p>
				</div>
			)}
		</div>
	);
}

export default GameScreen;
